import { ROLE_LABELS } from '@/lib/roles';
import type { Org, Role } from '@/types';

/**
 * Utility to normalize a path by removing trailing slashes.
 */
export function normalizePath(path: string): string {
  return path.replace(/\/+$/, '');
}

/**
 * Utility function for robust route matching.
 * Returns true if `path` is a child route of `base`.
 */
export function isChildRoute(base: string, path: string): boolean {
  const normalizedBase = normalizePath(base);
  const normalizedPath = normalizePath(path);

  if (normalizedPath === normalizedBase) {
    // Exact match is not considered a child route
    return false;
  }

  return normalizedPath.startsWith(`${normalizedBase}/`);
}

/**
 * Filters an org list by name or ID against a search query.
 * Returns all orgs when query is empty.
 */
export function filterOrgs(orgs: Org[], query: string): Org[] {
  const q = query.trim().toLowerCase();

  if (!q) return orgs;

  return orgs.filter(o => o.name.toLowerCase().includes(q) || String(o.id).toLowerCase().includes(q));
}

/**
 * Formats a role string using centralized role labels.
 * Falls back to snake_case splitting for roles not yet in ROLE_LABELS.
 * Example: "org_admin" -> "Org Admin" (via labels), "future_role" -> "Future Role"
 */
export function formatRoleLabel(role: string): string {
  // Check if it's a known Role and has a label
  if (role in ROLE_LABELS) {
    return ROLE_LABELS[role as Role];
  }
  // Unknown role: title-case the snake_case wire value rather than show it raw.
  return role
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
